import { useMemo } from "react";
import { renderBroadcastCell } from "./broadcast";
import { ConferenceLink } from "./ConferenceLink";
import { TeamLink } from "./TeamLink";
import { TeamLogo } from "./TeamLogo";
import { useFetch } from "./useFetch";
import { shiftYMD, todayYMD } from "./useURLState";
import type {
  BroadcastsResponse,
  ConferencesResponse,
  Game,
  GameSide,
  GameState,
  ScoreboardResponse,
  TeamsResponse,
} from "./types";

// splitYMD breaks a YYYY-MM-DD string into zero-padded (yyyy, mm, dd)
// pieces suitable for the scoreboard upstream URL, which demands each
// component as a separate path segment.
function splitYMD(ymd: string): [string, string, string] {
  const [y, m, d] = ymd.split("-");
  return [y, m, d];
}

// isUpstreamUnavailable returns true when the fetch error's message
// starts with a 503. useFetch throws `new Error("<status> <text>")`
// on non-ok responses, so a string prefix match is the current
// contract. Any other status (400, 500, network) falls through to the
// technical error path so bugs stay visible during development.
function isUpstreamUnavailable(message: string): boolean {
  return message.startsWith("503 ");
}

// formatKickoff renders a startTime ISO as "7:00 PM EST" in the
// user's local timezone. The timezone abbreviation is deliberate —
// without it, a bare time is ambiguous (is 7 PM Eastern or
// Pacific?), and the whole point of displaying kickoff is for the
// reader not to miss the match. "TBA" when the upstream didn't
// publish a time or the backend returned the zero timestamp.
function formatKickoff(iso: string): string {
  if (!iso) return "TBA";
  const t = new Date(iso);
  if (Number.isNaN(t.getTime())) return "TBA";
  if (t.getUTCFullYear() < 1900) return "TBA";
  return t.toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    timeZoneName: "short",
  });
}

const SPORT_LABEL: Record<string, string> = {
  "soccer-women": "Women's Soccer",
  "soccer-men": "Men's Soccer",
  football: "Football",
  "basketball-women": "Women's Basketball",
  "basketball-men": "Men's Basketball",
};

// Scoreboard upstream (NCAA.com proxy) emits hyphen-cased slugs
// (`north-carolina`, `nc-state`); the catalog YAML registry uses
// underscore-cased slugs (`north_carolina`). Keyed by both forms so a
// single Map lookup resolves either. If neither hits, fall back to a
// title-cased rendering of the hyphen slug — avoids showing the raw
// slug in the table while a mapping gap gets filled.
function buildNameLookup(teams: TeamsResponse | null): Map<string, string> {
  const m = new Map<string, string>();
  if (!teams) return m;
  for (const t of teams.teams) {
    m.set(t.slug, t.shortName);
    m.set(t.slug.replace(/_/g, "-"), t.shortName);
  }
  return m;
}

function titleCase(slug: string): string {
  return slug
    .split("-")
    .filter((p) => p.length > 0)
    .map((p) => p[0].toUpperCase() + p.slice(1))
    .join(" ");
}


// buildConferenceLookup maps conference slugs (both hyphen and
// underscore forms) to a (shortName, fullName) pair so the scoreboard
// can show the abbreviation inline and the full name as a hover
// tooltip. The scoreboard proxy emits the hyphen form on each
// GameSide; the catalog's conferences endpoint keys by the
// underscore form — both forms resolve to the same label pair.
type ConfLabel = { short: string; full: string };

function buildConferenceLookup(
  r: ConferencesResponse | null,
): Map<string, ConfLabel> {
  const m = new Map<string, ConfLabel>();
  if (!r) return m;
  for (const c of r.conferences) {
    const label: ConfLabel = { short: c.shortName, full: c.name };
    m.set(c.slug, label);
    m.set(c.slug.replace(/_/g, "-"), label);
  }
  return m;
}

// ScoreboardPageProps feeds the Schedule tab. Sport and division are
// owned by App.tsx so a single header selector drives every page.
// `date` is YYYY-MM-DD; "" means today (and stays off the URL so
// /schedule reads clean on the common case). `conference` is the
// shared URL-state filter used by Standings too; empty = no filter.
export type ScoreboardPageProps = {
  sport: string;
  division: string;
  date: string;
  onDateChange: (d: string) => void;
  conference: string;
  onConferenceChange: (c: string) => void;
};

// scoreboardConfProps bundles the sport/division context down to the
// per-row renderer so the Conf cells can emit conference deep-links
// without each row re-reading state from the page.
type GameListProps = {
  games: Game[];
  nameFor: (slug: string) => string;
  confFor: (slug: string) => ConfLabel;
  broadcastFor: (gameID: string) => { channel: string; url: string };
  sport: string;
  division: string;
};

export function ScoreboardPage({
  sport,
  division,
  date,
  onDateChange,
  conference,
  onConferenceChange,
}: ScoreboardPageProps) {
  // Empty date means "today" — canonicalise once at the top so every
  // downstream reference (URL, label, picker value) sees a concrete
  // YYYY-MM-DD. The URL stays clean because buildURL omits ?date=
  // whenever the field is "" (see useURLState).
  const effectiveDate = date || todayYMD();
  // updateDate strips the date back to "" when it equals today so a
  // user clicking "next" from yesterday lands on /schedule, not
  // /schedule?date=<today>.
  const updateDate = (ymd: string) => {
    onDateChange(ymd === todayYMD() ? "" : ymd);
  };
  const [y, m, d] = splitYMD(effectiveDate);
  const url = `/api/v1/scoreboard/${sport}/${division}/${y}/${m}/${d}`;
  const state = useFetch<ScoreboardResponse>(url);
  const teamsState = useFetch<TeamsResponse>(
    `/api/v1/teams?sport=${encodeURIComponent(sport)}&division=${encodeURIComponent(division)}`,
  );
  const confsState = useFetch<ConferencesResponse>("/api/v1/conferences");
  // Broadcast sidecar: scoreboard carries no `broadcast` field upstream;
  // the standings service's schedule-ingest + ESPN phases populate
  // per-game TV metadata. All three tabs (Yesterday / Today /
  // Tomorrow) render the Broadcast column so a user scanning
  // historical scores alongside live games sees the same cell shape.
  const broadcastsURL = `/api/v1/broadcasts/${sport}/${division}/${y}/${m}/${d}`;
  const broadcastsState = useFetch<BroadcastsResponse>(broadcastsURL);
  const dateLabel = new Date(Number(y), Number(m) - 1, Number(d)).toLocaleDateString(
    undefined,
    { weekday: "short", month: "short", day: "numeric" },
  );

  const nameFor = useMemo(() => {
    const map = buildNameLookup(
      teamsState.kind === "ok" ? teamsState.data : null,
    );
    return (slug: string): string => map.get(slug) ?? titleCase(slug);
  }, [teamsState]);

  const broadcastFor = useMemo(() => {
    // Map keyed by scoreboard game id → {channel, url}. Scoreboard
    // and the broadcasts endpoint use the same NCAA id so this is a
    // plain lookup, not a date+slug fuzzy join.
    const map = new Map<string, { channel: string; url: string }>();
    if (broadcastsState.kind === "ok") {
      for (const b of broadcastsState.data.broadcasts) {
        map.set(b.id, { channel: b.broadcast ?? "", url: b.broadcastUrl ?? "" });
      }
    }
    return (gameID: string) => map.get(gameID) ?? { channel: "", url: "" };
  }, [broadcastsState]);

  const confFor = useMemo(() => {
    const map = buildConferenceLookup(
      confsState.kind === "ok" ? confsState.data : null,
    );
    // Returns both the short abbreviation (for the cell text) and the
    // full name (for the hover tooltip). Unknown slugs fall back to a
    // best-effort render so the column never shows an empty cell.
    return (slug: string): ConfLabel => {
      if (!slug) return { short: "", full: "" };
      const hit = map.get(slug);
      if (hit) return hit;
      const fallback = slug.toUpperCase().replace(/[-_]/g, " ");
      return { short: fallback, full: fallback };
    };
  }, [confsState]);

  // conferenceOptions builds the dropdown's option list from the
  // conferences present on the current day's games. Keeping options
  // dynamic (instead of listing every D1 conference) prevents users
  // from picking a conference that would guarantee an empty list on
  // this specific day. Each option carries both the slug (URL value)
  // and the short label (display). The option set changes per day so
  // conferences without games stay out of the dropdown.
  const conferenceOptions = useMemo(() => {
    if (state.kind !== "ok") return [] as { slug: string; label: string }[];
    const seen = new Map<string, string>();
    for (const g of state.data.games) {
      for (const side of [g.home, g.away] as const) {
        const slug = side.conference ?? "";
        if (!slug || seen.has(slug)) continue;
        seen.set(slug, confFor(slug).short);
      }
    }
    return Array.from(seen, ([slug, label]) => ({ slug, label })).sort((a, b) =>
      a.label.localeCompare(b.label),
    );
  }, [state, confFor]);

  // filteredGames narrows the day's games to those involving a team
  // from the selected conference (either side). Conference-cross
  // games count because the user looking for an ACC game expects to
  // see ACC-vs-SEC too — the filter is about "a team I follow is
  // playing," not "both teams are in my conference." The scoreboard
  // emits conference as the NCAA conferenceSeo slug on each side,
  // which matches the catalog's conference slugs post migration
  // (mvc / ovc / uac_ aligned in PR #152).
  const filteredGames = useMemo(() => {
    if (state.kind !== "ok") return [];
    if (!conference) return state.data.games;
    return state.data.games.filter(
      (g) => g.home.conference === conference || g.away.conference === conference,
    );
  }, [state, conference]);

  const activeConferenceLabel = useMemo(() => {
    if (!conference) return "";
    const hit = conferenceOptions.find((o) => o.slug === conference);
    return hit?.label ?? confFor(conference).short;
  }, [conference, conferenceOptions, confFor]);

  return (
    <section>
      <div className="page-head">
        <h2>
          {SPORT_LABEL[sport] ?? sport} · {division.toUpperCase()} ·{" "}
          <span className="muted">{dateLabel}</span>
          {conference ? (
            <>
              {" · "}
              <span className="muted">{activeConferenceLabel}</span>
            </>
          ) : null}
        </h2>
        {state.kind === "ok" && (
          <span
            className="count-badge"
            role="status"
            aria-label={`${filteredGames.length} ${filteredGames.length === 1 ? "game" : "games"} on ${dateLabel}`}
          >
            {filteredGames.length}{" "}
            {filteredGames.length === 1 ? "game" : "games"}
          </span>
        )}
      </div>

      <div className="controls">
        <div className="date-nav" role="group" aria-label="Schedule date">
          <button
            type="button"
            className="date-nav-step"
            onClick={() => updateDate(shiftYMD(effectiveDate, -1))}
            aria-label="Previous day"
          >
            ◀
          </button>
          <input
            type="date"
            className="date-nav-input"
            value={effectiveDate}
            onChange={(e) => updateDate(e.target.value || todayYMD())}
            aria-label="Select date"
          />
          <button
            type="button"
            className="date-nav-step"
            onClick={() => updateDate(shiftYMD(effectiveDate, 1))}
            aria-label="Next day"
          >
            ▶
          </button>
        </div>
        <label
          className={conference ? "header-select is-active" : "header-select"}
        >
          <span className="visually-hidden">Conference</span>
          <select
            value={conference}
            onChange={(e) => onConferenceChange(e.target.value)}
            aria-label="Conference"
          >
            <option value="">All conferences</option>
            {conferenceOptions.map((o) => (
              <option key={o.slug} value={o.slug}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
      </div>

      {conference && state.kind === "ok" && (
        <div className="filter-badge" role="status">
          <span>
            Filtering by <strong>{activeConferenceLabel}</strong>
          </span>
          <button
            type="button"
            className="filter-badge-clear"
            onClick={() => onConferenceChange("")}
            aria-label={`Clear ${activeConferenceLabel} filter`}
          >
            Clear ×
          </button>
        </div>
      )}

      {state.kind === "loading" && <p>Loading…</p>}
      {state.kind === "error" &&
        (isUpstreamUnavailable(state.message) ? (
          // A 503 means the scoreboard service's upstream failed and the
          // stale cache had nothing to fall back to — from the reader's
          // perspective there's just no visible data for the day. The
          // technical status code is useful in logs, not in the UI.
          <p className="muted">No games or matches for this day.</p>
        ) : (
          <p role="alert">Failed to load scoreboard: {state.message}</p>
        ))}
      {state.kind === "ok" && filteredGames.length === 0 && (
        <p className="muted">No games or matches for this day.</p>
      )}
      {state.kind === "ok" && filteredGames.length > 0 && (
        <GameList
          games={filteredGames}
          nameFor={nameFor}
          confFor={confFor}
          broadcastFor={broadcastFor}
          sport={sport}
          division={division}
        />
      )}
    </section>
  );
}

// stateOrder groups the scoreboard list so live matches always sit at
// the top. Final and scheduled fall out in that order so a user
// skimming the page sees results of games that have already happened
// before upcoming kickoffs. Postponed/canceled sink below the three
// primary states because they carry no live-or-coming-up signal — a
// user doesn't need to track them in the same visual region.
const stateOrder: Record<GameState, number> = {
  live: 0,
  final: 1,
  scheduled: 2,
  postponed: 3,
  canceled: 4,
};

// sortGames orders games by state first (live → final → scheduled →
// postponed → canceled) and by kickoff ascending within each state.
// Games with an empty startTime (TBA) sort to the end of their group
// so a known 7pm kickoff reads before an unknown one.
function sortGames(games: Game[]): Game[] {
  const out = games.slice();
  out.sort((a, b) => {
    const sa = stateOrder[a.state] ?? 99;
    const sb = stateOrder[b.state] ?? 99;
    if (sa !== sb) return sa - sb;
    // Within a group, kickoff ascending. Empty startTime → +Infinity
    // so TBA games sink to the end of their group.
    const ta = a.startTime ? new Date(a.startTime).getTime() : Number.POSITIVE_INFINITY;
    const tb = b.startTime ? new Date(b.startTime).getTime() : Number.POSITIVE_INFINITY;
    if (ta !== tb) return ta - tb;
    // Final tiebreaker: id, so the order is stable across renders
    // even when two live games have identical start times.
    return a.id.localeCompare(b.id);
  });
  return out;
}

function GameList({
  games,
  nameFor,
  confFor,
  broadcastFor,
  sport,
  division,
}: GameListProps) {
  // Sort once per games change — live first, then final, then
  // scheduled, each sub-group ordered by kickoff. See stateOrder for
  // the rationale behind the state ordering.
  const sortedGames = useMemo(() => sortGames(games), [games]);

  // Compute the widest team-name string across both columns and give
  // both <col> elements that width. Without this the two team columns
  // sized independently and the one holding the longer names grew
  // visibly wider than the other, breaking the symmetric away/home
  // layout. Rank prefixes ("#3 ") can precede a name; count them so
  // the measured width still fits once the badge is rendered.
  const teamColWidth = useMemo(() => {
    let max = 0;
    for (const g of sortedGames) {
      const a = nameFor(g.away.teamSlug).length + (g.away.rank > 0 ? 4 : 0);
      const h = nameFor(g.home.teamSlug).length + (g.home.rank > 0 ? 4 : 0);
      if (a > max) max = a;
      if (h > max) max = h;
    }
    // Chars → ch units; add a small buffer so proportional-digit
    // ranks don't crowd the name. Clamped to a floor so an empty or
    // tiny set still has a sane column.
    return `${Math.max(max + 1, 10)}ch`;
  }, [sortedGames, nameFor]);

  if (sortedGames.length === 0) {
    return <p className="empty">No games scheduled.</p>;
  }
  return (
    <div className="table-scroll">
    <table className="today">
      <colgroup>
        <col className="state-col" />
        <col className="kickoff-col" />
        <col className="conf-col" />
        <col className="logo-col" />
        <col className="team-col" style={{ width: teamColWidth }} />
        <col className="score-col" />
        <col className="at-col" />
        <col className="score-col" />
        <col className="team-col" style={{ width: teamColWidth }} />
        <col className="logo-col" />
        <col className="conf-col" />
        <col className="broadcast-col" />
      </colgroup>
      <thead>
        <tr>
          <th className="state-col">State</th>
          <th className="kickoff-col">Kickoff</th>
          <th className="conf-col">Conf</th>
          <th className="logo-col"></th>
          <th className="team-col">Away</th>
          <th className="score-col"></th>
          <th className="at-col"></th>
          <th className="score-col"></th>
          <th className="team-col">Home</th>
          <th className="logo-col"></th>
          <th className="conf-col">Conf</th>
          <th className="broadcast-col">Broadcast</th>
        </tr>
      </thead>
      <tbody>
        {sortedGames.map((g) => {
          const awayOutcome = outcomeClass(g.away, g.home, g.state);
          const homeOutcome = outcomeClass(g.home, g.away, g.state);
          return (
          <tr key={g.id} className={g.state === "live" ? "live" : undefined}>
            <td className="state-col">
              <span className={`state ${g.state}`}>{g.state}</span>
            </td>
            <td className="kickoff-col kickoff">{formatKickoff(g.startTime)}</td>
            {(() => {
              const c = confFor(g.away.conference ?? "");
              const slug = g.away.conference ?? "";
              return (
                <td className="conf-col muted">
                  {slug ? (
                    <ConferenceLink
                      conference={slug}
                      sport={sport}
                      division={division}
                      label={c.short}
                      title={c.full || undefined}
                    />
                  ) : (
                    <span title={c.full || undefined}>{c.short}</span>
                  )}
                </td>
              );
            })()}
            <td className="logo-col">
              <TeamLogo slug={g.away.teamSlug} alt={nameFor(g.away.teamSlug)} />
            </td>
            <td className={`team-col ${awayOutcome}`}>
              {g.away.rank > 0 && <span className="rank">#{g.away.rank}</span>}
              <TeamLink
                slug={g.away.teamSlug}
                sport={sport}
                division={division}
                title={`Open ${nameFor(g.away.teamSlug)} team page`}
              >
                {nameFor(g.away.teamSlug)}
              </TeamLink>
            </td>
            <td className={`score-col ${awayOutcome}`}>
              {displayScore(g.away.score, g.state)}
            </td>
            <td className="at-col">@</td>
            <td className={`score-col ${homeOutcome}`}>
              {displayScore(g.home.score, g.state)}
            </td>
            <td className={`team-col ${homeOutcome}`}>
              {g.home.rank > 0 && <span className="rank">#{g.home.rank}</span>}
              <TeamLink
                slug={g.home.teamSlug}
                sport={sport}
                division={division}
                title={`Open ${nameFor(g.home.teamSlug)} team page`}
              >
                {nameFor(g.home.teamSlug)}
              </TeamLink>
            </td>
            <td className="logo-col">
              <TeamLogo slug={g.home.teamSlug} alt={nameFor(g.home.teamSlug)} />
            </td>
            {(() => {
              const c = confFor(g.home.conference ?? "");
              const slug = g.home.conference ?? "";
              return (
                <td className="conf-col muted">
                  {slug ? (
                    <ConferenceLink
                      conference={slug}
                      sport={sport}
                      division={division}
                      label={c.short}
                      title={c.full || undefined}
                    />
                  ) : (
                    <span title={c.full || undefined}>{c.short}</span>
                  )}
                </td>
              );
            })()}
            {(() => {
              // Prefer the schedule-ingest sidecar's channel/url over
              // the upstream scoreboard `broadcast` field — the NCAA
              // proxy ships that empty for WSoc on every day we have
              // seen. `renderBroadcastCell` owns the four-branch
              // link-resolution logic shared with the team-page
              // schedule table.
              const b = broadcastFor(g.id);
              return (
                <td className="broadcast-col muted">
                  {renderBroadcastCell({
                    gameID: g.id,
                    channel: b.channel || g.broadcast || "",
                    url: b.url,
                  })}
                </td>
              );
            })()}
          </tr>
          );
        })}
      </tbody>
    </table>
    </div>
  );
}


function displayScore(score: number, state: string): string {
  if (state === "scheduled") return "–";
  return String(score);
}

// outcomeClass picks the row-level outcome marker for one side of a
// game. "winner" bolds + accent-colors the name and score; "loser"
// dims them so the winner reads as the result at a glance. "" leaves
// the cell at the default color — correct for live games (no settled
// outcome), scheduled games (no scores yet), and ties on a final
// game (both sides have winner=false, both stay default).
function outcomeClass(
  side: GameSide,
  otherSide: GameSide,
  state: GameState,
): "winner" | "loser" | "" {
  if (side.winner) return "winner";
  if (state === "final" && otherSide.winner) return "loser";
  return "";
}
